import { ErrorType, ErrorsList, Token } from 'types/entities'

import { StoredAirdropItem, StoredAirdropList } from 'state/airdrop/types'

import { checkIsNumber } from 'utils/helpers'
import { checkIsAddress, fromWei, getAddressListFromEns, toWei } from 'utils/crypto'

import { etherNumbers } from 'constants/variables'
import { errors as Errors } from 'constants/errors'

export type DuplicatesInfo = Record<string, StoredAirdropItem>

export async function validateRecipientsList(recipientsList: StoredAirdropList, token: Token) {
  let totalTokens = etherNumbers.BG_ZERO

  const { recipientsListWithoutEns, errors } = await getAddressListFromEns(recipientsList)

  for (let i = 0; i < recipientsListWithoutEns.length; i++) {
    const { addr, amount } = recipientsListWithoutEns[i]
    const lineLength = Object.values(recipientsListWithoutEns[i]).join('').length

    try {
      if (!addr) {
        throw new Error(Errors.validation.ADDRESS_IS_REQUIRED)
      }
      checkIsAddress(addr)
      checkIsNumber(amount, Number(token.decimals.toString()))

      const numDecimals = Number(token.decimals.toString())

      totalTokens = totalTokens + toWei(amount, numDecimals)
    } catch (err) {
      let errorType: ErrorType
      if (err.message.includes('amount')) {
        errorType = ErrorType.AMOUNT
      } else {
        errorType = ErrorType.ADDRESS
      }

      errors.push({ line: i, errorMessage: err.message, lineLength, type: errorType })
    }
  }

  return { errors, recipientsListWithoutEns, totalTokens }
}

// ToDo add ENS
export function findDuplicates(list: StoredAirdropList) {
  const duplicateListErrors: ErrorsList = []
  const duplicatesInfo: DuplicatesInfo = {}

  const duplicatesMap = new Map()

  const doubleList = list.reduce((acc: string[], curr: StoredAirdropItem, index) => {
    if (duplicatesMap.has(curr.addr)) {
      acc.push(curr.addr)

      if (!duplicatesInfo[curr.addr]) {
        const origin = duplicatesMap.get(curr.addr)
        duplicatesInfo[curr.addr] = { addr: curr.addr, amount: origin.amount } as StoredAirdropItem
      }

      const prevBalance = duplicatesInfo[curr.addr]?.amount || String(0)

      duplicatesInfo[curr.addr].addr = curr.addr
      duplicatesInfo[curr.addr].amount = fromWei((toWei(curr.amount) + toWei(prevBalance)).toString())

      duplicateListErrors.push({
        line: index,
        lineLength: 50,
        type: ErrorType.DUPLICATE,
        errorMessage: `${Errors.validation.DUPLICATE_ADDRESS}`,
      })
    } else {
      duplicatesMap.set(curr.addr, curr)
    }
    return acc
  }, [])

  const hasDuplicates = doubleList.length > 0

  return { hasDuplicates, duplicateListErrors, duplicatesInfo }
}

// ToDo add ENS
export function mergeDuplicates(recipientsList: StoredAirdropList, duplicates: DuplicatesInfo) {
  const _duplicates = Object.values(duplicates).map(({ addr, amount }) => ({ addr, amount }))
  return recipientsList.reduce((acc, curr) => {
    if (duplicates[curr.addr]) {
      return acc
    }
    acc.unshift(curr)
    return acc
  }, _duplicates)
}
